import { z } from '@hono/zod-openapi';

import { ChannelSchema, InstantSchema, UUIDSchema } from './common';

export const BackupSnapshotUploadSchema = z
  .object({
    snapshotId: UUIDSchema,
    envelopeVersion: z.literal(1),
    schemaVersion: z.number().int().positive(),
    ciphertextSha256: z.string().min(43).max(64),
    payload: z.string().min(32).max(70_000_000),
  })
  .strict()
  .openapi('BackupSnapshotUpload');

export const BackupSnapshotSchema = BackupSnapshotUploadSchema.omit({ payload: true })
  .extend({ createdAt: InstantSchema, sizeBytes: z.number().int().positive().max(52_428_800) })
  .strict()
  .openapi('BackupSnapshot');

export const BackupSnapshotListSchema = z
  .object({ snapshots: z.array(BackupSnapshotSchema).max(20), requestId: UUIDSchema })
  .strict()
  .openapi('BackupSnapshotList');

export const BackupSnapshotDownloadSchema = z
  .object({ snapshot: BackupSnapshotSchema, payload: z.string(), requestId: UUIDSchema })
  .strict()
  .openapi('BackupSnapshotDownload');

export const NotificationSubscriptionSchema = z
  .object({
    id: z.string().min(1).max(200),
    platform: ChannelSchema,
    endpoint: z.url().max(2_048).optional(),
    p256dh: z.string().min(16).max(512).optional(),
    auth: z.string().min(8).max(512).optional(),
    aitTokenCiphertext: z.string().min(16).max(4_096).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const web =
      value.platform === 'web' &&
      value.endpoint &&
      value.p256dh &&
      value.auth &&
      !value.aitTokenCiphertext;
    const ait =
      value.platform === 'ait' &&
      value.aitTokenCiphertext &&
      !value.endpoint &&
      !value.p256dh &&
      !value.auth;
    if (!web && !ait)
      context.addIssue({ code: 'custom', message: 'Platform subscription fields mismatch' });
  })
  .openapi('NotificationSubscription');

export const NotificationRuleSchema = z
  .object({
    id: UUIDSchema,
    type: z.enum(['study_time', 'registration', 'exam_day']),
    timezone: z.string().min(1).max(100),
    localTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .optional(),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    presetId: z.string().min(1).max(100).optional(),
    eventAt: InstantSchema.optional(),
    quietHours: z
      .object({
        startsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
        endsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
      })
      .strict(),
    enabled: z.boolean(),
    revision: z.number().int().positive(),
  })
  .strict()
  .openapi('NotificationRule');

export const NotificationRulesRequestSchema = z
  .object({ rules: z.array(NotificationRuleSchema).max(20) })
  .strict()
  .openapi('NotificationRulesRequest');

export const RoomSummarySchema = z
  .object({
    id: UUIDSchema,
    name: z.string().min(1).max(80),
    type: z.enum(['official', 'private']),
    capacity: z.union([z.literal(2), z.literal(4), z.literal(8)]),
    memberCount: z.number().int().nonnegative().max(8),
    status: z.enum(['open', 'closed']),
    mode: z.enum(['normal', 'conserve', 'camera_limited', 'provider_unavailable']),
  })
  .strict()
  .openapi('RoomSummary');

export const RoomListSchema = z
  .object({ rooms: z.array(RoomSummarySchema), requestId: UUIDSchema })
  .strict()
  .openapi('RoomList');

export const CreateRoomSchema = z
  .object({
    name: z.string().min(1).max(80),
    capacity: z.union([z.literal(2), z.literal(4), z.literal(8)]),
    codeVerifier: z.string().min(32).max(128),
  })
  .strict()
  .openapi('CreateRoom');

export const JoinRoomSchema = z
  .object({
    consented: z.literal(true),
    code: z.string().min(4).max(32).optional(),
    publishCamera: z.literal(false),
    subject: z.string().trim().min(1).max(60).optional(),
    goal: z.string().trim().min(1).max(120).optional(),
  })
  .strict()
  .openapi('JoinRoom');

export const RoomJoinResponseSchema = z
  .object({
    room: RoomSummarySchema,
    memberId: z.string().min(8).max(100),
    token: z.string().min(16).max(2_048),
    memberIds: z.array(z.string().min(8).max(100)).max(8),
    joinedAt: InstantSchema,
    requestId: UUIDSchema,
  })
  .strict()
  .openapi('RoomJoinResponse');

export const RoomLeaveSchema = z
  .object({
    memberId: z.string().min(8).max(100),
    outcome: z.enum(['discarded', 'prefill_saved', 'disconnected']),
  })
  .strict()
  .openapi('RoomLeave');

export const RoomReportSchema = z
  .object({
    targetMemberId: z.string().min(8).max(100),
    reasonCode: z.enum(['camera_scope', 'harassment', 'identity', 'other']),
  })
  .strict()
  .openapi('RoomReport');

export const RoomKickSchema = z
  .object({ reasonCode: z.enum(['camera_scope', 'harassment', 'identity', 'other']) })
  .strict()
  .openapi('RoomKick');

export const RoomReactionSchema = z
  .object({ memberId: z.string().min(8).max(100), reaction: z.enum(['focus', 'break', 'cheer']) })
  .strict()
  .openapi('RoomReaction');

export const RoomPresenceRequestSchema = z
  .object({
    memberId: z.string().min(8).max(100),
    subject: z.string().trim().min(1).max(60).optional(),
    goal: z.string().trim().min(1).max(120).optional(),
  })
  .strict()
  .openapi('RoomPresenceRequest');

export const RoomParticipantSchema = z
  .object({
    memberId: z.string().min(8).max(100),
    joinedAt: InstantSchema,
    lastSeenAt: InstantSchema,
    subject: z.string().max(60).optional(),
    goal: z.string().max(120).optional(),
  })
  .strict()
  .openapi('RoomParticipant');

export const RoomReactionEventSchema = z
  .object({
    memberId: z.string().min(8).max(100),
    reaction: z.enum(['focus', 'break', 'cheer']),
    at: InstantSchema,
  })
  .strict()
  .openapi('RoomReactionEvent');

export const RoomPresenceResponseSchema = z
  .object({
    room: RoomSummarySchema,
    participants: z.array(RoomParticipantSchema).max(8),
    reactions: z.array(RoomReactionEventSchema).max(24),
    requestId: UUIDSchema,
  })
  .strict()
  .openapi('RoomPresenceResponse');

export const CapabilityAckSchema = z
  .object({ accepted: z.boolean(), requestId: UUIDSchema })
  .strict()
  .openapi('CapabilityAck');

export type BackupSnapshotUpload = z.infer<typeof BackupSnapshotUploadSchema>;
export type RoomSummaryContract = z.infer<typeof RoomSummarySchema>;
